import type { Floor, ProjectState, Wall } from './types';
import { regularFloors } from './roofs/levels';

// ── Reparation des etages ──────────────────────────────────
// Historiquement, un projet sans etage recevait un RDC d'identifiant fixe « __rdc__ », commun a tous les
// projets : en base, cet identifiant n'appartient qu'a un seul projet, les autres perdaient leur RDC
// (masque tant que la liste d'etages etait vide). Ici : tout etage reference mais absent est recree avec un
// identifiant propre au projet, « __rdc__ » est renomme, les elements sans etage sont rattaches au RDC.

export const LEGACY_FLOOR_ID = '__rdc__';

type WithFloor = { floorId?: string };

/** Identifiants d'etage references par les elements du projet */
function referencedFloorIds(p: ProjectState): Set<string> {
  const ids = new Set<string>();
  const add = (id?: string) => { if (id) ids.add(id); };
  const lists: (WithFloor[] | undefined)[] = [p.walls, p.rooms, p.symbols, p.wires, p.openings, p.tremies, p.spaces, p.roomSeparators];
  for (const l of lists) for (const x of l || []) add(x.floorId);
  for (const s of p.staircases || []) { add(s.floorId); add(s.lowerFloorId); add(s.upperFloorId); }
  for (const r of p.roofs || []) { add(r.floorId); add(r.supportFloorId); }
  return ids;
}

/** Applique un renommage d'identifiants d'etage a tous les elements */
function remapFloorIds(p: ProjectState, map: Map<string, string>): ProjectState {
  if (!map.size) return p;
  const m = (id?: string) => (id && map.has(id) ? map.get(id)! : id);
  const fix = <T extends WithFloor>(l?: T[]) => l?.map(x => (x.floorId && map.has(x.floorId) ? { ...x, floorId: m(x.floorId) } : x));
  return {
    ...p,
    floors: p.floors.map(f => (map.has(f.id) ? { ...f, id: map.get(f.id)! } : f)),
    walls: fix(p.walls)!, rooms: fix(p.rooms)!, symbols: fix(p.symbols)!, wires: fix(p.wires)!, openings: fix(p.openings)!,
    tremies: fix(p.tremies)!, spaces: fix(p.spaces), roomSeparators: fix(p.roomSeparators),
    staircases: (p.staircases || []).map(s => ({ ...s, floorId: m(s.floorId)!, lowerFloorId: m(s.lowerFloorId)!, upperFloorId: m(s.upperFloorId)! })),
    roofs: (p.roofs || []).map(r => ({ ...r, floorId: m(r.floorId)!, supportFloorId: m(r.supportFloorId) })),
  };
}

/** Etages coherents : chaque etage reference existe, identifiants propres au projet, aucun element sans etage */
export function repairFloors(p: ProjectState, makeId: () => string): ProjectState {
  let out = p;
  const map = new Map<string, string>();
  const floors: Floor[] = [...(p.floors || [])];
  const known = new Set(floors.map(f => f.id));

  // 1. Identifiant partage « __rdc__ » : renomme (present ou seulement reference)
  const refs = referencedFloorIds(p);
  if (known.has(LEGACY_FLOOR_ID) || refs.has(LEGACY_FLOOR_ID)) map.set(LEGACY_FLOOR_ID, makeId());

  // 2. Etages references mais absents : recrees (RDC au niveau 0, sinon au-dessous des etages existants)
  const regular = floors.filter(f => f.kind !== 'toiture');
  for (const id of refs) {
    if (known.has(id)) continue;
    const isRdc = id === LEGACY_FLOOR_ID || !regular.some(f => f.level === 0);
    const level = isRdc && !regular.some(f => f.level === 0) ? 0 : Math.min(0, ...regular.map(f => f.level)) - 1;
    const floor: Floor = { id, name: isRdc ? 'RDC' : 'Niveau', level, height: 250 };
    floors.push(floor);
    regular.push(floor);
    known.add(id);
  }
  if (floors.length !== (p.floors || []).length) out = { ...out, floors };
  out = remapFloorIds(out, map);

  // 3. Elements sans etage : rattaches au RDC (etage courant le plus proche du niveau 0), cree au besoin
  const orphan = <T extends WithFloor>(l?: T[]) => l?.some(x => !x.floorId);
  const hasOrphans = [out.walls, out.rooms, out.symbols, out.wires, out.openings, out.spaces, out.roomSeparators]
    .some(l => orphan(l as WithFloor[] | undefined));
  let base = [...out.floors].filter(f => f.kind !== 'toiture').sort((a, b) => Math.abs(a.level) - Math.abs(b.level))[0];
  if (hasOrphans && !base && out.floors.length) {
    base = { id: makeId(), name: 'RDC', level: 0, height: 250 };
    out = { ...out, floors: [...out.floors, base] };
  }
  if (base) {
    if (hasOrphans) {
      const fill = <T extends WithFloor>(l?: T[]) => l?.map(x => (x.floorId ? x : { ...x, floorId: base.id }));
      out = {
        ...out,
        walls: fill(out.walls)!, rooms: fill(out.rooms)!, symbols: fill(out.symbols)!, wires: fill(out.wires)!,
        openings: fill(out.openings)!, spaces: fill(out.spaces), roomSeparators: fill(out.roomSeparators),
      };
    }
  }
  return out;
}

/**
 * Projet sans le contenu des etages masques (vue 3D, coupe). Les etages restent tous presents pour
 * le calcul des altitudes ; un toit disparait si son niveau ou son etage d'appui est masque.
 */
export function withoutHiddenFloors(p: ProjectState, hidden: string[]): ProjectState {
  if (!hidden.length) return p;
  const h = new Set(hidden);
  const keep = <T extends WithFloor>(l?: T[]) => l?.filter(x => !x.floorId || !h.has(x.floorId));
  return {
    ...p,
    walls: keep(p.walls)!, rooms: keep(p.rooms)!, symbols: keep(p.symbols)!, wires: keep(p.wires)!,
    openings: keep(p.openings)!, tremies: keep(p.tremies)!, spaces: keep(p.spaces), roomSeparators: keep(p.roomSeparators),
    furniture: keep(p.furniture), terraces: keep(p.terraces), platforms: keep(p.platforms),
    staircases: (p.staircases || []).filter(s => !h.has(s.lowerFloorId) && !h.has(s.floorId)),
    roofs: (p.roofs || []).filter(r => !h.has(r.floorId) && !h.has(r.supportFloorId ?? r.floorId)),
  };
}

/** Le projet tel que le montre la vue 3D, pour l'export et le rendu : sans ses etages masques (FIX-036), ni son mobilier
 * s'il est masque (FIX-084) */
export function asShownIn3D(p: ProjectState, view: { hiddenFloorIds: string[]; showFurniture: boolean }): ProjectState {
  const q = withoutHiddenFloors(p, view.hiddenFloorIds);
  return view.showFurniture ? q : { ...q, furniture: [] };
}

/** Etages, du plus bas au plus haut, hors niveau Toiture (qui n'est pas un etage : il porte les toits) */
export function staircaseFloors(floors: Floor[]): Floor[] {
  return regularFloors(floors).sort((a, b) => a.level - b.level);
}

// ── Hauteur des murs d'un etage ────────────────────────────
// Un mur nait a la hauteur de son etage (modifiable ensuite, mur par mur) : un mur plus haut que son etage
// traverse l'etage du dessus — un sous-sol de 1 m remontait dans le RDC (FIX-052).

/** Hauteur (cm) d'un nouveau mur sur cet etage : celle de l'etage, 250 cm s'il est inconnu */
export function wallHeightOnFloor(floors: Pick<Floor, 'id' | 'height'>[], floorId: string): number {
  return floors.find(f => f.id === floorId)?.height || 250;
}

/** Copie de murs sur un autre etage, a sa hauteur ; idMap : ancien identifiant -> nouveau (pour les ouvertures) */
export function copyWallsToFloor(walls: Wall[], floorId: string, height: number, newId: () => string): { walls: Wall[]; idMap: Map<string, string> } {
  const idMap = new Map<string, string>();
  const copies = walls.map(w => {
    const id = newId();
    idMap.set(w.id, id);
    return { ...w, id, floorId, height };
  });
  return { walls: copies, idMap };
}

// ── Inserer un etage contre un etage de reference ──────────
// Le RDC (niveau 0) reste au sol : une insertion decale seulement les etages du cote oppose au RDC — au-dessus
// du RDC ils montent, en dessous les sous-sols descendent (decision du 2026-10-31 de `ETG` — Etage de reference).

/** Nom propose pour un niveau : « RDC », « Étage 1 », « Sous-sol », « Sous-sol 2 »… */
export function proposedFloorName(level: number): string {
  if (level === 0) return 'RDC';
  if (level > 0) return `Étage ${level}`;
  return level === -1 ? 'Sous-sol' : `Sous-sol ${Math.abs(level)}`;
}

/** Un nom est-il celui que l'editeur propose pour ce niveau ? Avec ou sans accent (« Etage 1 », ancienne forme) */
export function isProposedFloorName(name: string, level: number): boolean {
  const plain = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return plain(name) === plain(proposedFloorName(level));
}

/**
 * Place d'un nouvel etage au-dessus ou en dessous de l'etage de reference : son niveau, le nouveau niveau de chaque
 * etage a decaler (jamais le niveau Toiture, qui se recale ensuite au-dessus), et les deux etages entre lesquels il
 * s'insere (lower, upper : l'etage de reference et son voisin, s'il en a un de ce cote).
 */
export function floorInsertion(floors: Floor[], referenceId: string, direction: 'above' | 'below'): {
  level: number; shifted: Map<string, number>; lower?: Floor; upper?: Floor;
} {
  const regular = staircaseFloors(floors);
  const shifted = new Map<string, number>();
  const i = regular.findIndex(f => f.id === referenceId);
  if (i < 0) return { level: direction === 'above' ? 1 : -1, shifted };
  const ref = regular[i];
  const [lower, upper] = direction === 'above' ? [ref, regular[i + 1]] : [regular[i - 1], ref];
  const target = direction === 'above' ? ref.level + 1 : ref.level - 1;
  // Le niveau voisin est libre : on s'y place, rien ne bouge
  if (!regular.some(f => f.level === target)) return { level: target, shifted, lower, upper };
  const between = direction === 'above' ? [ref.level, ref.level + 1] : [ref.level - 1, ref.level];
  if (between[0] >= 0) {
    // Au-dessus du sol : le nouvel etage prend le niveau du haut, les etages a partir de lui montent
    for (const f of regular) if (f.level >= between[1]) shifted.set(f.id, f.level + 1);
    return { level: between[1], shifted, lower, upper };
  }
  // Sous le sol : le nouvel etage prend le niveau du bas, les etages a partir de lui descendent
  for (const f of regular) if (f.level <= between[0]) shifted.set(f.id, f.level - 1);
  return { level: between[0], shifted, lower, upper };
}

/**
 * Etage montre en filigrane sous l'etage actif : son voisin du cote du RDC — l'etage inferieur au-dessus du sol, l'etage
 * superieur pour un sous-sol, comme le promet la fenetre de creation ; le RDC voit le sous-sol s'il y en a un (FIX-031).
 */
export function ghostFloorId(floors: Floor[], activeId: string): string | undefined {
  const active = floors.find(f => f.id === activeId);
  if (!active) return undefined;
  const regular = floors.filter(f => f.kind !== 'toiture' && f.id !== active.id);
  const below = regular.filter(f => f.level < active.level).sort((a, b) => b.level - a.level)[0];
  const above = regular.filter(f => f.level > active.level).sort((a, b) => a.level - b.level)[0];
  return active.level < 0 ? above?.id : below?.id;
}

/** L'etage du sol : le RDC (niveau 0), sinon le plus bas des etages ; jamais le niveau Toiture (FIX-035) */
export function groundFloorId(floors: Floor[]): string | undefined {
  const regular = floors.filter(f => f.kind !== 'toiture');
  return (regular.find(f => f.level === 0) ?? [...regular].sort((a, b) => a.level - b.level)[0])?.id;
}

/** Altitude (m) du plancher de chaque etage, le RDC (ou le plus bas) a 0 ; sous-sols en negatif */
export function computeFloorElevations(floors: Floor[]): Map<string, number> {
  const sorted = [...floors].sort((a, b) => a.level - b.level);
  if (sorted.length === 0) return new Map();

  // Trouver le RDC (level 0). Si absent, le floor le plus bas fait office de reference.
  const rdcIdx = sorted.findIndex(f => f.level === 0);
  const refIdx = rdcIdx >= 0 ? rdcIdx : 0;

  const map = new Map<string, number>();

  // Du ref vers le haut
  let y = 0;
  for (let i = refIdx; i < sorted.length; i++) {
    map.set(sorted[i].id, y);
    y += sorted[i].height / 100 + (sorted[i].slabThickness ?? 0) / 100;
  }

  // Du ref vers le bas (sous-sols)
  y = 0;
  for (let i = refIdx - 1; i >= 0; i--) {
    y -= sorted[i].height / 100 + (sorted[i].slabThickness ?? 0) / 100;
    map.set(sorted[i].id, y);
  }

  return map;
}

/** Ce que montre la vue 3D, pour l'export et le rendu : ses etages masques et l'etage dont le plafond est masque (CAM) */
export interface View3DVisibility {
  hiddenFloorIds: string[];
  hideCeilingOf?: string;
}

/**
 * Etages masques en 3D a son ouverture (CAM) : ceux masques en 2D, plus tous ceux au-dessus de l'etage actif, niveau
 * Toiture compris — on voit l'interieur de l'etage actif. Propre a la 3D : la 2D garde ses etages masques.
 */
export function floorsHiddenIn3D(floors: Floor[], activeId: string, hidden2D: string[]): string[] {
  const active = floors.find(f => f.id === activeId);
  if (!active) return [...hidden2D];
  const above = floors.filter(f => f.id !== active.id && (f.kind === 'toiture' || f.level > active.level)).map(f => f.id);
  return [...new Set([...hidden2D, ...above])];
}

/**
 * Plafond (dalle haute) de l'etage actif masque en 3D : tant que ce qui repose dessus est masque — l'etage juste au-dessus,
 * ou pour le dernier etage le niveau Toiture (le toit est pose sur la dalle) — ou qu'il n'y a rien au-dessus.
 */
export function ceilingHiddenIn3D(floors: Floor[], activeId: string, hidden3D: string[]): string | undefined {
  const active = floors.find(f => f.id === activeId);
  if (!active) return undefined;
  const next = floors.filter(f => f.kind !== 'toiture' && f.level > active.level).sort((a, b) => a.level - b.level)[0]
    ?? floors.find(f => f.kind === 'toiture');
  return !next || hidden3D.includes(next.id) ? active.id : undefined;
}
